'use client'

import type { PropsWithChildren } from 'react'
import { useState } from 'react'

import { VisionMobileMenu } from '@common/core/components/MobileMenu/variants/VisionMobileMenu'
import { useLayoutStore } from '@common/core/stores'

import { VisionSidebar } from '@/core/components/Sidebar/Sidebar'
import { useLibraries } from '@/core/hooks/useLibraries'
import { useRegisterVisionUrl } from '@/core/hooks/useRegisterVisionUrl'
import { useVisionLibraryTree } from '@/core/hooks/useVisionLibraryTree'
import { selectPathString, useNavigationStore } from '@/core/stores/navigationStore'

import { LayoutHeader, LayoutMainContent } from './components'
import { VISION_MENU_GROUPS } from './const'
import { useLayoutActions } from './hooks'

import '@styles/layout/layout.scss'

/** Vision app layout: sidebar, header, main content, mobile menu. */
export function LayoutClient({ children }: PropsWithChildren) {
  const { isVisionLocal } = useLayoutStore()
  const { gatewayDisplayName, isLoading, isError, hasNoModules, refetch } = useLibraries()
  useRegisterVisionUrl()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const pathString = useNavigationStore(selectPathString)
  const treeItems = useVisionLibraryTree()

  const {
    handleSignUpClick,
    handleRefresh,
    handleCreateGateway,
    handleMenuToggle,
    handleMobileMenuClose,
    handleProfileClick,
    handleNavigationClick,
    handleTreeItemClick,
    handleTreeItemToggle,
  } = useLayoutActions({ setMobileMenuOpen, refetch })

  return (
    <div className={`app-layout ${isError ? 'is-error' : ''} ${hasNoModules ? 'is-empty' : ''}`}>
      <aside className="sidebar">
        <VisionSidebar />
      </aside>

      <div className="main-content">
        <LayoutHeader
          isError={isError}
          isLoading={isLoading}
          gatewayName={gatewayDisplayName}
          onRefetchGateway={refetch}
          onProfileClick={handleProfileClick}
          onMenuToggle={handleMenuToggle}
        />

        <LayoutMainContent
          isError={isError}
          isLoading={isLoading}
          isVisionLocal={isVisionLocal}
          pathString={pathString}
          onRefetchGateway={refetch}
          onRefresh={handleRefresh}
          onCreateGateway={handleCreateGateway}
          onSignUpClick={handleSignUpClick}
        >
          {children}
        </LayoutMainContent>
      </div>

      <VisionMobileMenu
        isOpen={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        menuGroups={VISION_MENU_GROUPS}
        onProfileClick={handleProfileClick}
        onNavigationClick={handleNavigationClick}
        treeItems={treeItems}
        activeItemId={pathString || undefined}
        onTreeItemClick={handleTreeItemClick}
        onTreeItemToggle={handleTreeItemToggle}
        isLoading={isLoading}
        isError={isError}
      />
    </div>
  )
}
